import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const lowLevelAlertErrorTypes = ['AuthorizeError', 'AuthenticateError', 'OAuthWindowClosed', 'OAuthWindowTimeout', 'OAuthWindowBlocked', 'MissingPermissions', 'UnknownOAuthProblem'];
function isKnownErrorType(errorType) {
  return lowLevelAlertErrorTypes.includes(errorType);
}
function getErrorAlertConfig(appName) {
  return {
    AuthorizeError: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.authorize.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.authorize.default"
      })
    },
    AuthenticateError: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.authenticate.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.authenticate.default"
      })
    },
    OAuthWindowBlocked: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowBlocked.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowBlocked.instructions",
        options: {
          appName
        }
      })
    },
    OAuthWindowClosed: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowClosed.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowClosed.instructions"
      })
    },
    OAuthWindowTimeout: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowTimedOut.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.oauthWindowTimedOut.instructions"
      })
    },
    UnknownOAuthProblem: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.authenticate.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.pleaseTryAgain"
      })
    },
    MissingPermissions: {
      alertType: 'danger',
      title: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.missingPermissions.titleText",
        options: {
          appName
        }
      }),
      description: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unifiedAuthCore.errors.missingPermissions.body"
      })
    }
  };
}
export function LowLevelAuthErrorAlert({
  appName,
  error,
  context,
  processError
}) {
  var _config$description;
  const defaultConfig = useMemo(() => getErrorAlertConfig(appName), [appName]);
  const processedError = processError === null || processError === void 0 ? void 0 : processError({
    appName,
    error,
    defaultConfig,
    context
  });
  if ((processedError === null || processedError === void 0 ? void 0 : processedError.presentation) === 'component') {
    return processedError.render({
      error,
      context
    });
  }
  const effectiveError = processedError !== null && processedError !== void 0 && processedError.errorOverride && processedError.presentation === 'alert' ? processedError.errorOverride : error;
  const config = (() => {
    if ((processedError === null || processedError === void 0 ? void 0 : processedError.presentation) === 'alert') {
      return processedError.config;
    }
    if (isKnownErrorType(effectiveError.type)) {
      return defaultConfig[effectiveError.type];
    }
    console.warn(`[LowLevelAuthErrorAlert] Unknown error type "${effectiveError.type}" encountered. ` + 'Consider providing a processError handler for custom error types.');
    return undefined;
  })();
  if (!config) {
    return null;
  }
  const authenticateErrorDetails = effectiveError.type === 'AuthenticateError' ? effectiveError : undefined;
  const errorMessage = effectiveError.type === 'AuthorizeError' || effectiveError.type === 'AuthenticateError' ? effectiveError.message : undefined;
  return /*#__PURE__*/_jsxs(Alert, {
    type: config.alertType,
    titleText: config.title(appName),
    children: [errorMessage || ((_config$description = config.description) === null || _config$description === void 0 ? void 0 : _config$description.call(config, appName)), (authenticateErrorDetails === null || authenticateErrorDetails === void 0 ? void 0 : authenticateErrorDetails.upstreamErrorMessage) && /*#__PURE__*/_jsx(AccordionItem, {
      marginBlockStart: "400",
      title: /*#__PURE__*/_jsx("strong", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "unifiedAuthCore.errors.accountCreation.details"
        })
      }),
      children: /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "unifiedAuthCore.errors.accountCreation.htmlMessage",
          options: {
            message: authenticateErrorDetails.upstreamErrorMessage
          }
        })
      })
    })]
  });
}